/// <amd-module name="Build/DesignerExtensions/YamlAssistant/Components/SnippetList" />
import * as Resources from "Build/DesignerExtensions/YamlAssistant/Resources";
import * as React from "react";

import { ObservableValue } from 'VSS/Core/Observable';
import { equals, format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";

import { HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Image } from "VSSUI/Image";
import { Intersection } from "VSSUI/Intersection";
import { IListItemDetails, IListRow, List, ListItem, ListSelection, } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { TextField, TextFieldStyle } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TelemetryConstants, YamlAssistantToggleElement } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { FilteredSnippetListStore } from "Build/DesignerExtensions/YamlAssistant/Stores/FilteredSnippetListStore";
import { YamlSnippet } from "Pipelines/Common/Library/Contracts/DataProvider";

import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface SnippetListProps {
    onDismiss(): void;
    onSnippetSelected(snippet: YamlSnippet): void;
    getSnippets(): YamlSnippet[];
}

export interface SnippetListState {
    searchText: string;
    loading: boolean;
    selectedTabId: string;
    error?: Error;
}

export class SnippetList extends VssComponent<SnippetListProps, SnippetListState> {
    private _store: FilteredSnippetListStore;
    private _listRef = React.createRef<HTMLDivElement>();
    private _isScrolled = new ObservableValue<boolean>(false);
    private _unallowedTaskIdList = [ 
                                    "F1E4B0E6-017E-4819-8A48-EF19AE96E289" // Query work items task
                                ];

    constructor(props: SnippetListProps) {
        super(props);

        this.state = {
            searchText: "",
            loading: true,
            selectedTabId: "snippets"
        };
    }

    public componentDidMount() {
        super.componentDidMount();

        this._store = new FilteredSnippetListStore();
        this._store.addListener(this._getStateFromStore);

        this._loadSnippets();
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this._store.dispose();
    }

    public render(): JSX.Element {
        return <div className={"yaml-snippet-list flex-column flex-grow relative"}>
            <TabBar
                className={"snippet-list-tab-bar"}
                tabSize={TabSize.Compact}
                renderAdditionalContent={this._tabCommands}
                selectedTabId={this.state.selectedTabId}
                onSelectedTabChanged={this._onSelectedTabChanged}>
                <div role={"tab"} className="static-tab">{Resources.SnippetsTabLabel}</div>
                {/* <Tab id={"snippets"} name={Resources.SnippetsTabLabel} /> */}
            </TabBar>
            {this._content()}
        </div>;
    }

    private _content(): JSX.Element | null {
        if (this.state.error && this.state.error.message) {
            return <span>{format(Resources.FailedToLoadSnippet, this.state.error.message)}</span>
        }

        if (this.state.loading) {
            return <Spinner size={SpinnerSize.large} />;
        }

        return <>
            <Observer isScrolled={this._isScrolled}>
                {(observerProps: { isScrolled: boolean }) => (
                    <div
                        className={observerProps.isScrolled ? "scroll-decoration" : undefined}
                        role="presentation"
                        aria-hidden="true" />
                )}
            </Observer>
            <Intersection rootMargin={window.innerHeight / 2}>
                <div
                    className="flex-grow scroll-auto"
                    onScroll={this._onScroll}
                    ref={this._listRef} >
                    <div className={"snippet-search-container"}>
                        <TextField
                            value={this.state.searchText}
                            className={"snippet-search-input flex-grow"}
                            placeholder={Resources.SearchSnippetsLabel}
                            ariaLabel={Resources.SearchSnippetsLabel}
                            prefixIconProps={{ iconName: "Search" }}
                            onChange={(event, value) => this._onSearchTextChanged(value)}
                            style={TextFieldStyle.inline}
                        />
                    </div>
                    <List
                        itemProvider={this._getFilteredSnippets()}
                        renderRow={this._renderRow}
                        selection={new ListSelection()}
                        singleClickActivation={true}
                        onActivate={this._selectSnippet}
                        width={"100%"} />
                </div>
            </Intersection>
        </>
    }

    private _onScroll = (event: React.SyntheticEvent<HTMLElement>) => {
        if (this._listRef && this._listRef.current) {
            const isScrolled = this._listRef.current.scrollTop !== 0;
            if (this._isScrolled.value !== isScrolled) {
                this._isScrolled.value = isScrolled;
            }
        };
    }

    private _renderRow = (index: number, snippet: YamlSnippet, details: IListItemDetails<YamlSnippet>, key?: string): JSX.Element => {
        return <ListItem key={key || "list-item" + index} index={index} details={details}>
            <Tooltip text={snippet.description}>
                <div className="yaml-snippet-row">
                    <div className={"flex-row h-scroll-hidden"}>
                        <Image className={"yaml-snippet-icon"} src={snippet.iconUrl} alt=""/>
                        <div className={"snippet-row-main-column flex-column h-scroll-hidden"} >
                            <span className={"text-ellipsis"}>{snippet.friendlyName}</span>
                            <span className={"fontSize text-ellipsis secondary-text"}>
                                {snippet.description}
                            </span>
                        </div>
                    </div>
                </div>
            </Tooltip>
        </ListItem>;
    }

    private _getStateFromStore = () => {
        this.setState({
            searchText: this._store.getSearchText(),
            loading: this._store.isLoading()
        });
    }

    private _getFilteredSnippets() {
        // Hiding those tasks which are not needed to be shown in the snippet list.
        // We still allow the snippet to exist in store so that the existing configurations do not break.

        const filteredSnippets = this._store.getFilteredSnippets().filter(snippet => {
            return this._unallowedTaskIdList.findIndex(id => equals(id, snippet.id, true)) === -1;
        });

        return new ArrayItemProvider(filteredSnippets);
    }

    private _loadSnippets() {
        try {
            const snippets = this.props.getSnippets();
            this._store.loadSnippets(snippets);
        } catch (error) {
            this.setState({
                error
            });

            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService && telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.Error, {
                location: "loadSnippets",
                error
            });
        }
    }

    private _onSearchTextChanged(value: string) {
        this._store.filter(value);
    }

    private _selectSnippet = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<YamlSnippet>) => {
        // prevent the keyDown event that got us here from auto-clicking the back button as that is what will have focus right afterwards
        event.stopPropagation();
        event.preventDefault();

        const snippet = this._getFilteredSnippets().value[listRow.index];
        this.props.onSnippetSelected(snippet);

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService && telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.SnippetSelected, {
            name: snippet.friendlyName,
            version: snippet.version,
            isTemplate: Boolean(snippet.content),
        });
    }

    private _onSelectedTabChanged = (selectedTabId: string) => {
        this.setState({
            selectedTabId
        });

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService && telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.TabSwitch, {
            selectedTabId
        });
    }

    private _tabCommands = () => {
        return <HeaderCommandBar items={[
            {
                id: "hide-tasks",
                className: YamlAssistantToggleElement,
                onActivate: this.props.onDismiss,
                important: true,
                iconProps: { iconName: "ClosePane" },
                ariaLabel: Resources.CloseAssistant,
                subtle: true,
                tooltipProps: {
                    text: Resources.CloseAssistant
                }
            }
        ]} />;
    };
}
